// Iter 359 — Materiales con OC emitida y aún no recepcionados (nivel ítem)
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Truck, Search, RefreshCw, Loader2, ExternalLink, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV } from '../../lib/printUtils';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
const fmtFecha = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('-') : '-');

export default function PendientesRecepcion() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total_items: 0, total_ocs: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [ot, setOt] = useState('');
  const [debounced, setDebounced] = useState({ q: '', ot: '' });

  useEffect(() => {
    const t = setTimeout(() => setDebounced({ q: q.trim(), ot: ot.trim() }), 350);
    return () => clearTimeout(t);
  }, [q, ot]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bodega/items-pendientes-recepcion', { params: { q: debounced.q, ot: debounced.ot } });
      setItems(res.data.items || []);
      setMeta({ total_items: res.data.total_items || 0, total_ocs: res.data.total_ocs || 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando pendientes de recepción');
    } finally {
      setLoading(false);
    }
  }, [debounced]);
  useEffect(() => { load(); }, [load]);

  const exportar = () => exportToCSV({
    filename: 'materiales_pendientes_recepcion',
    title: 'CHIRINO STEEL · Materiales pendientes de recepción (OC emitida, sin recepcionar)',
    summary: [
      { label: 'Ítems pendientes', value: meta.total_items },
      { label: 'OCs con pendientes', value: meta.total_ocs },
      { label: 'Generado', value: new Date().toLocaleString('es-CL') },
    ],
    columns: [
      { key: 'numero_oc', label: 'OC' },
      { key: 'fecha_oc', label: 'Fecha OC', format: fmtFecha },
      { key: 'dias', label: 'Días' },
      { key: 'proveedor', label: 'Proveedor' },
      { key: 'ot_numero', label: 'OT' },
      { key: 'codigo_material', label: 'Código' },
      { key: 'descripcion', label: 'Descripción' },
      { key: 'unidad', label: 'Unidad' },
      { key: 'cantidad_oc', label: 'Cant. OC', format: (v) => Number(v) || 0 },
      { key: 'cantidad_recibida', label: 'Recibida', format: (v) => Number(v) || 0 },
      { key: 'cantidad_pendiente', label: 'Pendiente', format: (v) => Number(v) || 0 },
      { key: 'requisicion_numero', label: 'Requisición' },
    ],
    rows: items,
  });

  return (
    <div className="space-y-4" data-testid="pendientes-recepcion">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="shadow-sm" data-testid="kpi-items-pend-recep"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-50"><Truck className="w-5 h-5 text-teal-600" /></div>
          <div><p className="text-[11px] text-muted-foreground leading-tight">Ítems por recepcionar</p><p className="text-xl font-bold">{meta.total_items}</p></div>
        </CardContent></Card>
        <Card className="shadow-sm" data-testid="kpi-ocs-pend-recep"><CardContent className="p-3 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-50"><FileSpreadsheet className="w-5 h-5 text-indigo-600" /></div>
          <div><p className="text-[11px] text-muted-foreground leading-tight">OCs con pendientes</p><p className="text-xl font-bold">{meta.total_ocs}</p></div>
        </CardContent></Card>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar OC, proveedor, material, código…" className="h-8 pl-7 text-xs" data-testid="pend-recep-search" />
        </div>
        <Input value={ot} onChange={(e) => setOt(e.target.value)} placeholder="OT..." className="h-8 w-24 text-xs" data-testid="pend-recep-ot" />
        <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={load} disabled={loading} data-testid="pend-recep-refresh">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refrescar
        </Button>
        <Button variant="outline" size="sm" className="gap-1 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 ml-auto" onClick={exportar} disabled={items.length === 0} data-testid="pend-recep-excel">
          <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs" data-testid="tabla-pend-recep">
            <thead>
              <tr className="bg-gray-50 border-b text-left text-[11px] uppercase text-gray-500">
                <th className="p-2">OC</th>
                <th className="p-2">Fecha</th>
                <th className="p-2 text-right">Días</th>
                <th className="p-2">Proveedor</th>
                <th className="p-2">OT</th>
                <th className="p-2">Código</th>
                <th className="p-2">Descripción</th>
                <th className="p-2 text-right">Cant. OC</th>
                <th className="p-2 text-right">Recibida</th>
                <th className="p-2 text-right">Pendiente</th>
                <th className="p-2">Und</th>
                <th className="p-2">Req.</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr><td colSpan={13} className="p-8 text-center text-muted-foreground" data-testid="pend-recep-empty">No hay materiales pendientes de recepción</td></tr>
              )}
              {items.map((r) => (
                <tr key={`${r.numero_oc}-${r.item}`} className={`border-b hover:bg-gray-50 ${(r.dias || 0) > 30 ? 'bg-red-50/40' : ''}`} data-testid={`pend-recep-row-${r.numero_oc}-${r.item}`}>
                  <td className="p-2 font-mono font-semibold">{r.numero_oc}</td>
                  <td className="p-2 font-mono whitespace-nowrap">{fmtFecha(r.fecha_oc)}</td>
                  <td className={`p-2 text-right font-mono ${(r.dias || 0) > 30 ? 'text-red-600 font-bold' : ''}`}>{r.dias ?? '-'}</td>
                  <td className="p-2 max-w-[180px] truncate" title={r.proveedor}>{r.proveedor}</td>
                  <td className="p-2 font-mono">{r.ot_numero || '-'}</td>
                  <td className="p-2 font-mono text-[10px] text-gray-500">{r.codigo_material || '-'}</td>
                  <td className="p-2 max-w-[300px] truncate" title={r.descripcion}>{r.descripcion}</td>
                  <td className="p-2 text-right">{fmt(r.cantidad_oc)}</td>
                  <td className={`p-2 text-right ${r.parcial ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>{fmt(r.cantidad_recibida)}</td>
                  <td className="p-2 text-right font-bold text-teal-700">{fmt(r.cantidad_pendiente)}</td>
                  <td className="p-2">{r.unidad}</td>
                  <td className="p-2 font-mono text-gray-500">{r.requisicion_numero || '-'}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button onClick={() => navigate(`/compras/ordenes-compra?open=${r.numero_oc}`)} className="text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1" title="Abrir la OC (desde ahí puedes hacer la Recepción exprés)" data-testid={`pend-recep-ver-oc-${r.numero_oc}-${r.item}`}>
                      <ExternalLink className="w-3.5 h-3.5" /> OC
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
